import PlaneRestraint from "oc-interaction/restraints2/PlaneRestraint";
import Maths3 from "oc-threejs/Maths3";
import {v3} from "oc-threejs/short3";
import Commons from "oc-commons/Commons";
import ElementLineInABoxRestraint from "../ElementLineInABoxRestraint";
import AnyRestraint from "oc-interaction/restraints2/AnyRestraint";
import InteractionHandler from "../InteractionHandler";
import KonstruktionsbodenSnap from "../KonstruktionsbodenSnap";
import Fit from "../Fit";
import Element from "../../arrangement/Element";
import ClosedInterval from "oc-commons/ClosedInterval";

/**
 * @abstract
 */
export default class RFAbstract {

    static dragBorderSnap = 200;

    constructor(interactionHandler) {
        this.interactionHandler = interactionHandler;
    }

    get dragObject3d() {
        return this.interactionHandler.dragObject3d;
    }

    getRestraints() {
        return [];
    }

    _addRoomAndRemoveRestraints(restraints) {
        return this._getRoomRestraints().concat(this._maybeRemoveFromElementRestraint(restraints));
    }

    _getRoomRestraints() {
        const restraints = [
            // der Boden
            new PlaneRestraint({
                plane: Maths3.plane.y0,
                priority: 0,
            }),
        ];
        APP.arrangement.schraenke.forEach(schrank => {
            restraints.push(
                // die Drag-Ebene vor dem Schrank
                new PlaneRestraint({
                    parentObject3D: schrank.object3d,
                    plane: new THREE.Plane().setFromNormalAndCoplanarPoint(
                        v3(0, 0, 1),
                        v3(0, 0, schrank.box.max.z + 10)),
                    priority: 0,
                }),
            );
        });
        return restraints;
    }

    _maybeRemoveFromElementRestraint(restraints) {
        // Falls es keinen möglichen Dropbereich gibt, muss das Dragobjekt aus seinem Element entfernt werden.
        // Eigentlich stellt sich die Frage, warum es dann überhaupt in einem Element war. Aber es darf jedenfalls
        // nicht im Element bleiben.
        if (Commons.isEmpty(restraints)) {
            restraints.push(new (class extends AnyRestraint {
                onDrag3dBegin() {
                    InteractionHandler.detachFromElement(this.dragObject3d);
                }
            })());
        }
        return restraints;
    }

    _mapFitsToRestraints(ranges_: { fit: Fit, element: Element, box: THREE.Box3 }[], {fitDragIntoBox, restrictToElement}) {
        const restraints = [];

        // die Element-Boxen und deren Fits müssen linear vom kleinsten zum größten y-Wert durchlaufen werden,
        // um durchgehende Restraint-Boxen zu erzeugen. Angrenzende Restraint-Boxen berühren sich immer in der Mitte
        // zwischen Min- und Max der angrenzenden Element-Boxen.
        // Weil durch Trennwandboxen auch Elemente horizontal nebeneinander liegen können, wird ein scanline-artiges
        // Verfahren benutzt, um die Elementboxen in jedem "Slot" einzeln von unten nach oben zu durchlaufen:
        //
        // Elemente:
        // +---------+
        // |    3    |
        // +----+----+
        // |  1 | 2  |
        // +----+----+
        //
        // Daraus vertikale Slots mit y-sortierter Elementliste
        // |----|----|
        //  1,3   2,3

        const rangesPerSchrank = Commons.segments(ranges_,r=>r.element.schrank);
        //TODO: Vielleicht wackelig...
        const schrank = ranges_[ranges_.length - 1]?.element.schrank;

        rangesPerSchrank.forEach(ranges=>{

            const makeSlotRanges = ()=>{
                const brett_staerke = schrank?.options.getFloat('brett_staerke');
                const minimalSlotLength = brett_staerke * 2;
                const intervals = new PriorityIntervals();
                let i = 0;
                ranges.forEach(r => intervals.add(r.box.min.x, r.box.max.x, i++));
                const rangesSorted = _.sortBy(ranges, r => r.fit.minCollapse);
                const slotXIntervals = intervals.partition().finite().list.filter(i => i[1] - i[0] > minimalSlotLength);
                return slotXIntervals.map(s => rangesSorted.filter(r => ClosedInterval.intersects([r.box.min.x, r.box.max.x], s)));
            };

            const slotRanges = makeSlotRanges();

            slotRanges.forEach(ranges => {
                const drags = [];
                const firstRange = _.first(ranges);
                if (firstRange && !firstRange.visited) {
                    // der erste Eintrag ist ein Dummy, um einen Startpunkt zu haben
                    // Math.max(element.schrank.box.y,element.box.min.y-dragBorderSnap)
                    drags.push({min: NaN, max: firstRange.box.min.y - RFAbstract.dragBorderSnap});
                }

                const lastRange = _.last(ranges);
                const lastRangeVisited = lastRange.visited==true;

                ranges.forEach(range => {
                    const fit = range.fit;
                    if (fit.minCollapse < fit.min) {
                        drags.push({
                            min: fit.minCollapse,
                            max: fit.minCollapse,
                            element: range.element,
                            box: range.box,
                            ignore: range.visited==true,
                        });
                    }
                    drags.push({
                        min: fit.min,
                        max: fit.max,
                        element: range.element,
                        box: range.box,
                        ignore: range.visited==true,
                    });
                    if (fit.max < fit.maxCollapse) {
                        drags.push({
                            min: fit.maxCollapse,
                            max: fit.maxCollapse,
                            element: range.element,
                            box: range.box,
                            ignore: range.visited==true,
                        });
                    }
                    range.visited = true;
                });

                if (lastRange && !lastRangeVisited) {
                    // der letzte Eintrag ist ein Dummy, um einen Endpunkt zu haben
                    drags.push({min: lastRange.box.max.y + RFAbstract.dragBorderSnap, max: NaN});
                }

                // Mappt die Intervalle auf Restraints
                for (let i = 1; i < drags.length - 1; i++) {
                    const drag = drags[i];
                    if (drag.ignore) {
                        continue;
                    }
                    const ll = drags[i - 1].max;
                    const l = drag.min;
                    const r = drag.max;
                    const rr = drags[i + 1].min;
                    restraints.push(this._createLineRestraint((ll + l) / 2, l, r, (r + rr) / 2, {
                        element: drag.element,
                        box: drag.box,
                        fitDragIntoBox,
                        restrictToElement,
                    }));
                }
            });

        });

        return restraints;
    }

    _createLineRestraint(min, snapMin, snapMax, max, {
        element, box, fitDragIntoBox = false, restrictToElement = false
    }) {

        let outerBox = box.clone();
        outerBox.min.y = Math.min(min,max);
        outerBox.max.y = Math.max(min,max);

        const snapBox = box.clone();
        snapBox.min.y = Math.min(snapMin,snapMax);
        snapBox.max.y = Math.max(snapMin,snapMax);
        if (fitDragIntoBox) {
            snapBox.min.y = snapBox.max.y = snapBox.getCenter().y;
        }

        const restraint = new ElementLineInABoxRestraint({
            element: element,
            parentObject3D: element.child3dContainer,
            box: element.schrankToLocalBox(outerBox),
            snapBox: element.schrankToLocalBox(snapBox),
            priority: 1,
            newHeight: fitDragIntoBox ? snapMax - snapMin : null,
            restrictToElement,
        });

        // const m = (t,b) => {
        //     if (Math.abs(b.min.x-b.max.x)<1) {
        //         console.log(t+' '+b);
        //     }
        // }
        // m('outerBox',outerBox),m('snapBox',snapBox),m('box',box),m('schrankToLocalBox_outerBox',element.schrankToLocalBox(outerBox));
        // console.log(''+restraint.line,''+restraint.box,restraint);

        return restraint;
    }

    _getSnapYsInElement(element, fits, draggedKonstruktionsboedenDeltas, yTransformation = y => y) {
        //console.log(element, fits, draggedKonstruktionsboedenDeltas, yTransformation )
        if (element == null) {
            return [];
        }
        const elements = APP.arrangement.getElements();
        const snapYsInElement = [];
        APP.arrangement.iterateElements(e => {
            if (e !== element) {
                e.konstruktionsboeden.forEach(kb => {
                    draggedKonstruktionsboedenDeltas.forEach(delta => {
                        const y = kb.schrankBox.getCenter().y + delta;
                        if (fits.some(fit => fit.contains(y))) {
                            snapYsInElement.push(y);
                        }
                    });
                })
            }
        });
/*        elements/.filter(e => e !== element)/.forEach(e =>{
            e.konstruktionsboeden.forEach(kb => {
                draggedKonstruktionsboedenDeltas.forEach(delta => {
                    const y = kb.schrankBox.getCenter().y + delta;
                    if (fits.some(fit => fit.contains(y))) {
                        snapYsInElement.push(y);
                    }
                });
            })
        });*/
        const brett_staerke = element.schrank.options.getFloat('brett_staerke');
        _.uniqWith(snapYsInElement, (a, b) => Math.abs(a - b) <= brett_staerke);
        return snapYsInElement.map(y => new KonstruktionsbodenSnap({element, y, yTransformation}));
    }

}
